import styled from 'styled-components';
import noop from 'utils/noop';
import { useDispatch } from 'react-redux';

import { WorkflowType } from 'modules/workflows/enums';
import { Workflow } from 'modules/workflows/types';
import { fetchWorkflowRequest } from 'modules/workflows/ducks';
import { SecondaryButton } from 'ui/button';
import { Tooltip } from 'ui/tooltip';
import { WorkflowLiveToggle } from './WorkflowLiveToggle';
import { Actions } from '../Footer.styles';
import { LastSavedText } from './LastSavedText';

const StyledTooltipContent = styled.div`
  text-align: center;
`;

type PublishedActionsProps = {
  canDraftMessaging: boolean;
  canManageMessaging: boolean;
  isWorkflowEnabled: boolean;
  onCreateDraft?: () => void;
  onLiveToggleClick: () => void;
  workflow: Pick<Workflow, 'id' | 'workflowReference'>;
  updatedAt: string;
};
export const PUBLISHED_ACTIONS_TEST_ID = 'action-bar-published-mode';
export const VIEW_DRAFT_TEST_ID = 'view-draft-button';
export const EDIT_WORKFLOW_TEST_ID = 'edit-workflow-button';
export const MENU_DATA_TEST_ID = 'menu-button';
export const BUTTON_TEXT = {
  VIEW: 'View drafted edits',
  EDIT: 'Edit journey',
};

export const PublishedActions = ({
  canDraftMessaging = false,
  canManageMessaging = false,
  isWorkflowEnabled = false,
  onCreateDraft = noop,
  onLiveToggleClick = noop,
  workflow,
  updatedAt,
}: PublishedActionsProps) => {
  const { workflowReference } = workflow;

  const hasDraft =
    !!workflowReference &&
    workflowReference?.id &&
    workflowReference?.workflowType === WorkflowType.DRAFT;

  const dispatch = useDispatch();

  const handleDraftButtonClick = () => {
    if (hasDraft) {
      dispatch(
        fetchWorkflowRequest(
          workflowReference?.id,
          workflowReference?.workflowType as WorkflowType,
        ),
      );
    }
  };

  return (
    <Actions data-test={PUBLISHED_ACTIONS_TEST_ID}>
      <LastSavedText customText={'Last published'} timestamp={updatedAt} />
      {hasDraft ? (
        <SecondaryButton
          isDisabled={!canDraftMessaging}
          data-test={VIEW_DRAFT_TEST_ID}
          onClick={handleDraftButtonClick}
        >
          {BUTTON_TEXT.VIEW}
        </SecondaryButton>
      ) : (
        <Tooltip
          tooltipContent={
            <StyledTooltipContent>
              This will create a separate draft of your journey. Your contacts won’t see these
              changes until you publish them.
            </StyledTooltipContent>
          }
        >
          <SecondaryButton data-test={EDIT_WORKFLOW_TEST_ID} onClick={onCreateDraft}>
            {BUTTON_TEXT.EDIT}
          </SecondaryButton>
        </Tooltip>
      )}
      <WorkflowLiveToggle
        isDisabled={!canManageMessaging}
        isActive={isWorkflowEnabled}
        onClick={onLiveToggleClick}
      />
    </Actions>
  );
};
